import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getChats, selectChatPagination, selectChats } from "../../../routes/Chat/chat.ducks";
import styles from "../chatPage.scss";
import { ChatsListItem } from "./ChatsListItem";
import { inboxType, UserI } from "../../../typings/interfaces";
import { Separator } from "./Separator";
import { FiltersCard } from "./FiltersCard";
import { Button, Card, Checkbox, Pagination, Select } from "antd";
import { useDeleteChats } from "../hooks/useDeleteChats";
import { calculateTotalPagingCount, getWindowWidth, isMobileScreen, scrollToTop, getCroatianPluralForm } from "../../../services";
import HelmetComponent from "../../../utils/HelmetHelper";
import { useChatListFilters } from "../hooks/useChatListFilters";
import { CHAT_ITEM_PER_PAGE, CHAT_LIST_OPTIONS, PAGE_PARAM } from "../constants/constants";
import { FilterIcon } from "./FilterIcon";
import { useHistory, useLocation } from "react-router-dom";
import Skeleton from "../../../components/Skeleton";

type Props = {
  user: UserI;
  inboxTypeParam?: number;
  userParam?: string;
  adParam?: string;
  pageParam?: number;
}

export const ChatsList = ({ user, inboxTypeParam, userParam, adParam, pageParam }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const chats = useSelector(selectChats);
  const chatPagination = useSelector(selectChatPagination)
  const isMobile = isMobileScreen(getWindowWidth());

  const {
    isAllSelected,
    handleSelectAll,
    isDeleting,
    setSelectedChats,
    handleBatchDelete,
    selectedChats,
  } = useDeleteChats(chats)


  const loadChats = (params, restoreScroll = false) => {
    setIsLoading(true)
    dispatch(getChats(params, () => {
      setIsLoading(false);
      if (restoreScroll && location.state && location.state.scrollPosition) {
        setTimeout(() => window.scrollTo(0, location.state.scrollPosition), 0);
      }
    }));
  }

  const {
    onInboxFilterChange,
    onUserFilterChange,
    onAdFilterChange
  } = useChatListFilters(loadChats)

  const [isLoading, setIsLoading] = useState(true);


  useEffect(() => {
    loadChats({ inboxType: inboxTypeParam, userId: userParam, adId: adParam, page: pageParam }, true)
  }, [])


  useEffect(() => {
    setSelectedChats([])
  }, [chats])



  const handleChatSelect = (chatId, checked) => {
    if (checked) {
      setSelectedChats(prev => [...prev, chatId]);
    } else {
      setSelectedChats(prev => prev.filter(id => id !== chatId));
    }
  }

  const handlePageNumberChange = (page: number) => {
    scrollToTop()
    const params = new URLSearchParams(window.location.search)
    if (page > 1) {
      params.set(PAGE_PARAM, String(page))
    } else {
      params.delete(PAGE_PARAM)
    }
    const newSearch = params.toString()
    history.replace({ search: newSearch ? `?${newSearch}` : "" })
    loadChats({
      inboxType: inboxTypeParam,
      page
    })
  }


  if (!user) {
    return null;
  }

  return (
    <React.Fragment>
      <HelmetComponent title="Poruke | INDEX OGLASI" index={false} />
      <FiltersCard
        onAdFilterChange={onAdFilterChange}
        onUserFilterChange={onUserFilterChange}
        isMobile={isMobile}
        onInboxFilterChange={onInboxFilterChange}
        userParam={userParam}
        adParam={adParam}
        inboxTypeParam={inboxTypeParam}
      />
      <Card className={styles.chatsListCard} bordered={false} >
        <div className={styles.chatsListFilterWrapper}>
          <div>
            <Checkbox
              checked={isAllSelected}
              onChange={(e) => handleSelectAll(e.target.checked)}
              style={{ paddingRight: 12 }}
            >
              {isAllSelected ? 'Odznači sve' : 'Označi sve'}
            </Checkbox>
            {selectedChats.length > 0 && (<span className={styles.selectedCount}>
              {selectedChats.length} {getCroatianPluralForm(selectedChats.length, "razgovor", "razgovora", "razgovora")}
            </span>)}
            {selectedChats.length > 0 && (
              <Button
                type="primary"
                loading={isDeleting}
                onClick={handleBatchDelete}
                style={{ marginLeft: 8 }}
              >
                Obriši
              </Button>
            )}
          </div>
          {!isMobile && (
            <div style={{ position: "relative", width: 240 }}>
              <FilterIcon color="primary" style={{ position: "absolute", left: 17, top: 19, zIndex: 1 }} />
              <Select
                onChange={onInboxFilterChange}
                value={inboxTypeParam}
                placeholder="Filtriraj"
                className={styles.chatsListDesktopFilter}
              >
                {CHAT_LIST_OPTIONS.map(option => (
                  <Select.Option key={option.value} value={option.value}>{option.title}</Select.Option>

                ))}
              </Select>
            </div>
          )}
        </div>
        <Separator />
        {isLoading && (
          <React.Fragment>
            {Array.from({ length: 5 }).map((_, i) => (
              <React.Fragment key={i}>
                <div className={styles.skeletonItem}>
                  <div className={`${styles.skeletonCheckbox} ${styles.skeletonLoader}`} />
                  <div className={styles.skeletonContent}>
                    <div className={`${styles.skeletonSender} ${styles.skeletonLoader}`} />
                    <div className={`${styles.skeletonTitle} ${styles.skeletonLoader}`} />
                    <div className={`${styles.skeletonDate} ${styles.skeletonLoader}`} />
                  </div>
                </div>
                {i < 4 && <div className={styles.skeletonSeparator} />}
              </React.Fragment>
            ))}
          </React.Fragment>
        )}
        {!isLoading && (
          <React.Fragment>
            {chats.length === 0 && <p className={styles.noChats}>Nemate započetih razgovora</p>}
            {chats.length > 0 && (
              chats.map((chat, index) => (
                <ChatsListItem
                  key={chat.chatId}
                  chat={chat}
                  user={user}
                  isSelected={selectedChats.includes(chat.chatId)}
                  onSelect={(checked) => handleChatSelect(chat.chatId, checked)}
                  isMobile={isMobile}
                  isLastItem={index === chats.length - 1}
                />
              ))
            )}
          </React.Fragment>
        )}
      </Card>
      {chatPagination && chatPagination.count > CHAT_ITEM_PER_PAGE && (
        <div style={{ display: "flex", justifyContent: "center", marginBottom: isMobile ? 20 : 0, marginTop: 20 }}>
          <Pagination
            current={chatPagination.currentPage}
            total={calculateTotalPagingCount(chatPagination.count, chatPagination.currentPage)}
            onChange={handlePageNumberChange}
            pageSize={CHAT_ITEM_PER_PAGE}
          />
        </div>
      )}

    </React.Fragment>
  )
}


// WEBPACK FOOTER //
// ./src/containers/ChatsPage/ChatsList/ChatsList.tsx